/* Copyright (c) 2020-present CloudIO Inc */

import { IoPages } from '@cloudio-saas/datasource-types';
import { useStateXSelector } from '@cloudio-saas/statex';
import { FormControl, FormHelperText } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { PageIDChooserProp } from 'componentregistry';
import { Option, VirtualizedSelectInput } from 'custom/SelectInput';
import { helpTextSelector } from 'designer/state';
import React, { memo, useEffect } from 'react';
import useAppStore from 'store/useAppStore';
import WaveDots from 'utils/WaveDots';
import { pagesFilter } from '../../PagesView/PagesPanel';
import { getDefaultValues, isRequired } from './PropertyEditorUtils';
import { PropertyEditorProps } from './types';

const useStyles = makeStyles({
  root: {
    marginTop: 8,
    '&:first-child': {
      marginTop: 0,
    },
  },
});

function PageIDChooserProperty({
  appUid,
  activeItemId,
  def,
  pageId,
  property,
  mgr,
  value = getDefaultValues<string>(mgr, property),
  onChange,
  platform,
  isViewOnlyUser = false,
}: PropertyEditorProps<PageIDChooserProp, string>) {
  const { query, records, isBusy } = useAppStore<IoPages>({
    appUid,
    pageId: '$$Sidebar$$',
    ds: 'IoPages',
    alias: 'IoPagesChooserAlias',
  });
  useEffect(() => {
    query({ props: pagesFilter(appUid) });
  }, [appUid, query]);
  const classes = useStyles();
  const helpText = useStateXSelector(helpTextSelector, {
    params: { appUid, pageId, itemId: activeItemId, property, type: mgr.type },
  });
  const handleChange = (ds: string) => {
    if (ds !== value) {
      onChange(property, ds);
    }
  };

  const options: Option[] = Object.values(records).map((row) => ({
    value: row.pageUid ?? '',
    label: row.pageName ?? '',
  }));
  const required = isRequired({
    platform,
    mgr,
    appUid,
    pageId,
    itemId: activeItemId,
    required: def.required,
  });

  return (
    <FormControl
      data-testid={property}
      required={required}
      fullWidth
      variant="standard"
      component="fieldset"
      className={classes.root}
    >
      <VirtualizedSelectInput
        value={value}
        options={options}
        onChange={handleChange}
        label={def.label}
        startIcon={isBusy ? <WaveDots /> : null}
        disabled={isBusy || isViewOnlyUser}
        placeholder={isBusy ? 'Loading ...' : 'Choose a page'}
        variant="standard"
      />
      <FormHelperText>{helpText}</FormHelperText>
    </FormControl>
  );
}

export default memo(PageIDChooserProperty);
